import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, Phone, ShieldCheck, ChevronRight } from "lucide-react";
import { SERVICE_DETAIL_PAGES } from "@/data/servicedetailpages";
import { CONTACT, SITE } from "@/data/content";
import { Words, Reveal } from "@/lib/motion";
import Magnetic from "@/components/Magnetic";
import Seo from "@/components/Seo";
import { scrollToId } from "@/components/SmoothScroll";
import {
    Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

export default function ServiceDetailPage({ pageKey }) {
    const d = SERVICE_DETAIL_PAGES[pageKey];

    useEffect(() => {
        window.scrollTo(0, 0);
        if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    }, [pageKey]);

    if (!d) return null;

    const jsonLd = [
        {
            "@context": "https://schema.org",
            "@type": "Service",
            serviceType: d.serviceType,
            name: d.h1,
            description: d.metaDesc,
            url: `${SITE.url}/${d.slug}`,
            areaServed: { "@type": "City", name: d.slug.includes("alsace") ? "Alsace" : "Strasbourg" },
            provider: {
                "@type": "LocalBusiness",
                name: SITE.name,
                telephone: SITE.phoneIntl,
                url: SITE.url + "/",
                image: SITE.url + "/logo-ign.png",
                address: {
                    "@type": "PostalAddress",
                    streetAddress: "32 allée de la Robertsau",
                    postalCode: "67000",
                    addressLocality: "Strasbourg",
                    addressCountry: "FR",
                },
            },
        },
        {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: d.faq.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
        },
        {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
                { "@type": "ListItem", position: 1, name: "Accueil", item: SITE.url + "/" },
                { "@type": "ListItem", position: 2, name: "Services", item: SITE.url + "/#services" },
                { "@type": "ListItem", position: 3, name: d.h1, item: `${SITE.url}/${d.slug}` },
            ],
        },
    ];

    return (
        <main data-testid={`service-detail-${d.slug}`}>
            <Seo
                title={d.metaTitle}
                description={d.metaDesc}
                canonical={`${SITE.url}/${d.slug}`}
                image={d.heroImg}
                jsonLd={jsonLd}
            />

            {/* BREADCRUMB */}
            <nav className="bg-navy/95 pt-28 pb-4 px-6 md:px-12" aria-label="Fil d'Ariane">
                <div className="max-w-5xl mx-auto flex items-center gap-2 text-xs text-white/50 font-body">
                    <Link to="/" className="hover:text-white transition-colors">Accueil</Link>
                    <ChevronRight className="w-3 h-3" />
                    <Link to="/#services" className="hover:text-white transition-colors">Services</Link>
                    <ChevronRight className="w-3 h-3" />
                    <span className="text-white/80">{d.serviceType}</span>
                </div>
            </nav>

            {/* HERO */}
            <section className="relative min-h-[65vh] flex items-center overflow-hidden bg-ink">
                <div className="absolute inset-0 z-0">
                    <img
                        src={d.heroImg}
                        alt={d.h1}
                        className="w-full h-full object-cover"
                        loading="eager"
                    />
                </div>
                <div className="absolute inset-0 z-[1]" style={{ background: "linear-gradient(180deg, rgba(6,21,41,0.85), rgba(6,21,41,0.7) 40%, rgba(6,21,41,0.93))" }} />
                <div className="absolute inset-0 z-[1] pointer-events-none" style={{ background: "radial-gradient(55% 50% at 20% 40%, rgba(46,123,255,0.2), transparent 70%)" }} />
                <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 pt-20 pb-24">
                    <motion.span initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }} className="inline-block text-[11px] uppercase tracking-[0.25em] text-electric-soft font-body mb-6 border border-white/20 px-4 py-1.5">
                        {d.kicker} · Strasbourg · Alsace
                    </motion.span>
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-[1.1] mb-7 font-display">
                        <span className="block overflow-hidden"><Words text={d.h1} delay={0.25} mount /></span>
                    </h1>
                    <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5, duration: 0.7 }} className="text-base md:text-lg text-white/70 font-body max-w-2xl leading-relaxed mb-8">
                        {d.intro[0].substring(0, 160)}...
                    </motion.p>
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7, duration: 0.7 }} className="flex flex-wrap gap-4">
                        <Magnetic strength={0.3}>
                            <button
                                type="button"
                                onClick={() => scrollToId("contact")}
                                className="group inline-flex items-center gap-3 bg-electric text-white px-8 py-4 text-sm uppercase tracking-wide font-semibold font-body hover:bg-white hover:text-navy transition-colors duration-300"
                                data-testid="service-detail-cta"
                            >
                                Demander un devis gratuit <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </button>
                        </Magnetic>
                        <a href={`tel:${CONTACT.phone}`} className="inline-flex items-center gap-2 border border-white/40 text-white px-8 py-4 text-sm uppercase tracking-wide font-semibold font-body hover:bg-white hover:text-navy transition-colors duration-300">
                            <Phone className="w-4 h-4" /> {CONTACT.phoneDisplay}
                        </a>
                    </motion.div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-brand-red z-10" />
            </section>

            {/* CONTENT SECTIONS */}
            <section className="py-20 md:py-28 bg-white">
                <div className="max-w-4xl mx-auto px-6 md:px-12">
                    {/* Intro paragraphs */}
                    {d.intro.map((p, i) => (
                        <Reveal key={i} delay={i * 0.06}>
                            <p className={"text-base md:text-lg leading-relaxed font-body " + (i === 0 ? "text-slate-700 mb-5" : "text-slate-600 mb-8")}>{p}</p>
                        </Reveal>
                    ))}

                    {/* Dynamic sections */}
                    {d.sections.map((sec, si) => (
                        <div key={si} className="mt-12">
                            <Reveal>
                                <h2 className="text-xl md:text-2xl font-medium text-navy font-display mb-6">{sec.title}</h2>
                            </Reveal>
                            {sec.text && (
                                <Reveal delay={0.05}>
                                    <p className="text-base leading-relaxed text-slate-600 font-body">{sec.text}</p>
                                </Reveal>
                            )}
                            {sec.items && (
                                <Reveal delay={0.05}>
                                    <ul className="space-y-3 mt-4">
                                        {sec.items.map((item) => (
                                            <li key={item} className="flex items-start gap-3 text-slate-600 font-body text-sm md:text-base">
                                                <Check className="w-5 h-5 text-electric flex-shrink-0 mt-0.5" />
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                </Reveal>
                            )}
                        </div>
                    ))}
                </div>
            </section>

            {/* ADVANTAGES */}
            <section className="py-20 bg-navy">
                <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
                    <Reveal>
                        <h2 className="text-2xl md:text-3xl font-medium text-white font-display mb-8">
                            Les avantages IGN Services Alsace
                        </h2>
                    </Reveal>
                    <div className="flex flex-wrap justify-center gap-3">
                        {d.advantages.map((e, i) => (
                            <Reveal key={e} delay={i * 0.04}>
                                <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium bg-white/10 border border-white/15 text-white font-body">
                                    <ShieldCheck className="w-4 h-4 text-electric-soft" /> {e}
                                </span>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="py-20 md:py-28 bg-white">
                <div className="max-w-3xl mx-auto px-6 md:px-12">
                    <Reveal className="text-center mb-10">
                        <span className="uppercase text-xs tracking-[0.2em] font-bold text-brand-red font-body">Questions fréquentes</span>
                        <h2 className="text-2xl md:text-3xl font-medium text-navy mt-4 font-display">{d.serviceType} à Strasbourg</h2>
                    </Reveal>
                    <Reveal delay={0.05}>
                        <Accordion type="single" collapsible className="w-full">
                            {d.faq.map((f, i) => (
                                <AccordionItem key={i} value={`faq-${i}`}>
                                    <AccordionTrigger className="text-left font-display text-navy text-base">{f.q}</AccordionTrigger>
                                    <AccordionContent className="text-sm text-slate-600 font-body leading-relaxed">{f.a}</AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </Reveal>
                </div>
            </section>

            {/* INTERNAL LINKS */}
            <section className="py-16 bg-mist border-t border-line">
                <div className="max-w-4xl mx-auto px-6 md:px-12">
                    <Reveal>
                        <h2 className="text-lg font-medium text-navy font-display mb-6">Prestations complémentaires</h2>
                    </Reveal>
                    <div className="flex flex-wrap gap-3">
                        {d.relatedPages.map((rp) => (
                            <Reveal key={rp.to} delay={0.03}>
                                <Link
                                    to={rp.to}
                                    className="inline-flex items-center gap-2 px-5 py-3 bg-white border border-line text-navy text-sm font-body font-medium hover:border-electric hover:text-electric transition-colors duration-300"
                                >
                                    {rp.label} <ArrowRight className="w-4 h-4" />
                                </Link>
                            </Reveal>
                        ))}
                        <Reveal delay={0.06}>
                            <Link
                                to="/"
                                className="inline-flex items-center gap-2 px-5 py-3 bg-white border border-line text-navy text-sm font-body font-medium hover:border-electric hover:text-electric transition-colors duration-300"
                            >
                                Tous nos services <ArrowRight className="w-4 h-4" />
                            </Link>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="py-24 bg-ink relative grain overflow-hidden">
                <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(60% 60% at 50% 30%, rgba(46,123,255,0.2), transparent 70%)" }} />
                <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
                    <Reveal>
                        <h2 className="text-3xl md:text-5xl font-light text-white font-display mb-6">
                            Besoin d'un devis ?
                        </h2>
                    </Reveal>
                    <Reveal delay={0.08}>
                        <p className="text-white/70 font-body mb-4">Réponse sous 24h, gratuite et sans engagement.</p>
                        <p className="text-white/50 font-body text-sm mb-10">
                            IGN Services Alsace · 32 allée de la Robertsau, 67000 Strasbourg · {CONTACT.phoneDisplay}
                        </p>
                    </Reveal>
                    <Reveal delay={0.12} className="flex flex-wrap justify-center gap-4">
                        <Magnetic strength={0.35}>
                            <Link to="/#contact" className="inline-flex items-center gap-3 bg-electric text-white px-9 py-4 text-sm uppercase tracking-wide font-semibold font-body hover:bg-white hover:text-navy transition-colors duration-300" style={{ boxShadow: "0 0 34px rgba(46,123,255,0.45)" }}>
                                Demander un devis gratuit <ArrowRight className="w-4 h-4" />
                            </Link>
                        </Magnetic>
                        <a href={`tel:${CONTACT.phone}`} className="inline-flex items-center gap-2 border border-white/40 text-white px-9 py-4 text-sm uppercase tracking-wide font-semibold font-body hover:bg-white hover:text-navy transition-colors duration-300">
                            <Phone className="w-4 h-4" /> {CONTACT.phoneDisplay}
                        </a>
                    </Reveal>
                </div>
            </section>
        </main>
    );
}
